<template>
    <vx-box
        v-if="metricData || loading || error"
        color="white"
        class="@container flex flex-col p-6 relative gap-5"
        :style="{
            minHeight: props.minHeight,
        }"
    >
        <div class="z-0 flex flex-row justify-between gap-4">
            <div class="space-y-px">
                <div class="flex items-baseline gap-2">
                    <span class="z-30 flex items-center justify-between font-semibold text-slate-800">
                        {{ loading ? 'Loading...' : metricTitle }}
                    </span>
                </div>
            </div>

            <vx-button
                v-if="!props.hideDetailsButton"
                class="hidden @xs:inline-flex"
                :disabled="loading"
                :href="`/analytics/franchise-hub/${props.category}/${props.metric}?${stringifyQueryString(
                    props.fetchParams
                )}`"
                size="sm"
                color="muted-light"
            >
                Details <font-awesome-icon widthAuto :icon="faSquareArrowUpRight" />
            </vx-button>
        </div>

        <card-loading-bars :loading="loading" />

        <div class="flex flex-col items-between gap-6 @xs:flex-row">
            <div v-if="!loading" class="flex gap-1">
                <div class="flex flex-col items-center w-full min-w-36 gap-2">
                    <dl
                        v-for="data in formattedMetricData"
                        :key="`${props.metric}_${data.label}`"
                        class="flex items-center justify-between w-full"
                    >
                        <dt class="inline-flex items-center text-muted">
                            <span class="truncate font-normal">{{ data.label }}:</span>
                        </dt>

                        <dd class="text-base flex flex-row items-center justify-between gap-2">
                            <span class="font-bold">{{ data.value }}</span>
                            <font-awesome-icon fixedWidth class="text-slate-500" :icon="data.icon" />
                        </dd>
                    </dl>
                </div>
            </div>

            <div
                v-if="!metricData && !loading"
                class="w-full h-full min-h-32 bg-slate-50 border border-dashed border-slate-200 rounded-md flex items-center justify-center"
            >
                <span class="text-sm text-slate-500">No data yet</span>
            </div>

            <div v-else-if="metricData && !loading" class="flex flex-row gap-1 w-full min-h-32">
                <div
                    :style="{ width: belowTargetPercent === aboveTargetPercent ? '50%' : `${belowTargetPercent}%` }"
                    class="min-w-max"
                >
                    <metric-chunk
                        :hover="metricData.num_below_target > 0"
                        @click="metricData.num_below_target > 0 && onChunkClicked({ above_target: false })"
                        class="min-w-max"
                        health="bad"
                        :size="1"
                        :aria-label="chunkLabel(metricData.num_below_target, false)"
                        data-test="chunk-below-target"
                    >
                        {{ metricData.num_below_target }}
                    </metric-chunk>
                </div>
                <div
                    :style="{ width: belowTargetPercent === aboveTargetPercent ? '50%' : `${aboveTargetPercent}%` }"
                    class="min-w-max"
                >
                    <metric-chunk
                        :hover="metricData.num_above_target > 0"
                        @click="metricData.num_above_target > 0 && onChunkClicked({ above_target: true })"
                        class="bg-sky-300! min-w-max"
                        health="good"
                        :size="1"
                        :aria-label="chunkLabel(metricData.num_above_target, true)"
                        data-test="chunk-above-target"
                    >
                        {{ metricData.num_above_target }}
                    </metric-chunk>
                </div>
            </div>

            <div class="block w-full @xs:hidden">
                <vx-button
                    class="w-full"
                    :disabled="loading"
                    size="sm"
                    color="muted-light"
                    :href="`/analytics/franchise-hub/${props.category}/${props.metric}?${stringifyQueryString(
                        props.fetchParams
                    )}`"
                >
                    Details <font-awesome-icon widthAuto :icon="faSquareArrowUpRight" />
                </vx-button>
            </div>
        </div>
    </vx-box>
</template>

<script setup>
import { VxBox, VxButton } from '@voxie/frontend-components';
import { computed, watch } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    faArrowDownToLine,
    faArrowUpToLine,
    faFlagCheckered,
    faHyphen,
    faSquareArrowUpRight,
} from '@fortawesome/pro-solid-svg-icons';
import { outboundMessagesAnalyticsClient } from '~/services/index';
import { useVersionedRequest } from '~/composables/useVersionedRequest';
import { addCategoryToFetchParams, metricLabel, metricsInputValidator, metricsWithGoals } from '~/utils/analytics';
import { formatPercentage, pluralized } from '~/components/filters';
import MetricChunk from '~/components/analytics/partial/MetricChunk.vue';
import { analyticsCategoriesValues } from '~/constants/categories';
import CardLoadingBars from '../CardLoadingBars.vue';
import { stringifyQueryString } from '~/utils/string';
import { sendFormRequest } from '~/utils/request';

const teamId = Spark.state.currentTeam.id;

const { loading, response, error, fetchData } = useVersionedRequest();

const props = defineProps({
    category: {
        type: String,
        required: true,
        validator: (category) => analyticsCategoriesValues.includes(category),
    },
    fetchParams: {
        type: Object,
        required: true,
        validator: metricsInputValidator,
    },
    minHeight: {
        type: String,
        default: '200px',
    },
    metric: {
        type: String,
        required: true,
        validator: (metric) => metricsWithGoals.includes(metric),
    },
    hideDetailsButton: {
        type: Boolean,
        default: false,
    },
});

const metricData = computed(() => {
    return !error.value
        ? response.value?.data?.data?.find(
              (result) => result.metric === props.metric && result.category === props.category
          )
        : undefined;
});

const formattedMetricData = computed(() => {
    return [
        {
            label: 'Goal',
            value: formatValue(metricData.value?.target),
            icon: faFlagCheckered,
        },
        {
            label: 'Best',
            value: formatValue(metricData.value?.best),
            icon: faArrowUpToLine,
        },
        {
            label: 'Median',
            value: formatValue(metricData.value?.median),
            icon: faHyphen,
        },
        {
            label: 'Worst',
            value: formatValue(metricData.value?.worst),
            icon: faArrowDownToLine,
        },
    ];
});

const totalGroupsNumber = computed(() =>
    metricData.value ? metricData.value.num_below_target + metricData.value.num_above_target : 0
);
const belowTargetPercent = computed(() =>
    metricData.value && metricData.value.num_below_target > 0
        ? (metricData.value.num_below_target / totalGroupsNumber.value) * 100
        : 0
);
const aboveTargetPercent = computed(() =>
    metricData.value && metricData.value.num_above_target > 0
        ? (metricData.value.num_above_target / totalGroupsNumber.value) * 100
        : 0
);

const metricTitle = computed(() => metricLabel(props.metric));

const formatValue = (value) => {
    return value || value === 0 ? formatPercentage(value, true) : '-';
};

watch(
    () => props.fetchParams,
    () => {
        const params = addCategoryToFetchParams(props.fetchParams, props.category);

        fetchData(outboundMessagesAnalyticsClient.getGroupPerformanceByCategory({ teamId }, params)).catch((e) => {
            console.error(e);
        });
    },
    { immediate: true, deep: true }
);

const onChunkClicked = (data) => {
    const url = new URL(`analytics/franchise-hub/${props.category}/${props.metric}/groups`, window.location.origin);
    url.search = stringifyQueryString(props.fetchParams);

    sendFormRequest(url.toString(), { ...data, target: metricData.value?.target });
};

const chunkLabel = (count, meetsGoal) =>
    count > 0 ? `${count} ${pluralized('group', count)} ${meetsGoal ? 'meeting' : 'not meeting'} the goal` : undefined;
</script>
